// Avatar asset registry. Parallel to AssetRegistry but for rigged
// characters that crowd users can pick from. Avatars carry animation
// metadata so the future 3D crowd system (step 4) can drive idle/dance
// loops by room energy.
//
// Storage:
//   - Source of truth: Supabase `avatars` table.
//   - Local cache: localStorage key `boothfm.avatars.v1` for instant first
//     paint (no network round-trip on cold reload).
//   - GLB bytes: Supabase Storage `avatars` bucket via BlobStore.

import { AVATAR_TABLE, supabase } from '../lib/supabase';
import type { GlbGeometry } from './types';

export interface AvatarDefinition {
  id: string;
  /** Display name. */
  name: string;
  /** Author note / tags. */
  description?: string;
  tags?: string[];
  /** The GLB geometry spec — same shape as venue AssetDefinition.geometry. */
  geometry: GlbGeometry;
  /** Clip names from the GLB that the DJ rig should randomly cycle through
   *  while on stage. Order isn't meaningful — the rotator picks at random.
   *  Empty / undefined means "no performance moves" and the rig will stay
   *  in bind pose. */
  clips?: string[];
  /** Optional hex color (e.g. '#ff8800'). Multiplied into every cloned
   *  material at buildAvatar time so the same source GLB can be re-used
   *  across visually-distinct avatars. Stashed inside the geometry JSONB
   *  on the server so no schema migration is required. */
  tint?: string;
  /** Optional per-material color overrides, keyed by Material.name from
   *  the GLB. Each entry REPLACES that material's base color (vs. `tint`
   *  which multiplies onto every material). Lets a single rigged GLB
   *  fan out into characters that differ in chosen body/armor/eye colors,
   *  not just an overall hue. */
  tints?: Record<string, string>;
  /** If true, this avatar shows up in the room picker / NPC seeding.
   *  Falsy (including undefined / explicit false) means draft — admin
   *  can still see + edit it, but end users can't choose it. */
  published?: boolean;
  /** If true, this avatar was uploaded by a user (vs. built-in). Persisted. */
  userCreated?: boolean;
  uploadedAt?: number;
}

const CACHE_KEY = 'boothfm.avatars.v1';

type RegistryListener = (event: { type: 'register' | 'remove'; id: string }) => void;

interface AvatarRow {
  id: string;
  name: string;
  description: string | null;
  tags: string[] | null;
  geometry: GlbGeometry;
  /** Stored in the `animations` JSONB column. Historically held a
   *  mood-keyed map ({ idle, walk, dance, clap, cheer, bored }); the rig
   *  is now mood-agnostic so we write a plain `string[]` of clip names.
   *  rowToDef flattens legacy rows into the same array shape, so the
   *  in-memory def never sees the old key/value form. */
  animations: string[] | Record<string, string | undefined> | null;
  user_created: boolean;
  uploaded_at: number;
}

function rowToDef(row: AvatarRow): AvatarDefinition {
  // tint, tints and published piggyback on the geometry JSONB so we can
  // iterate without a schema migration. Lift to the top level and strip
  // from the in-memory geometry so the def has a single source of truth —
  // defToRow re-stitches them back in on save.
  const { tint, tints, published, ...cleanGeo } = row.geometry as GlbGeometry & {
    tint?: string;
    tints?: Record<string, string>;
    published?: boolean;
  };
  return {
    id: row.id,
    name: row.name,
    description: row.description ?? undefined,
    tags: row.tags ?? undefined,
    geometry: cleanGeo as GlbGeometry,
    clips: clipsFromStoredAnimations(row.animations),
    tint,
    tints,
    published,
    userCreated: row.user_created,
    uploadedAt: row.uploaded_at,
  };
}

/** Flatten the JSONB `animations` column into the new clips array. Handles:
 *    - already-migrated rows: a plain `string[]`
 *    - legacy mood-keyed rows: `{ idle: "Idle", dance: "Dance", ... }` —
 *      every previously-mapped clip becomes part of the rotation pool
 *    - null / unexpected shape: empty list */
function clipsFromStoredAnimations(
  raw: string[] | Record<string, string | undefined> | null | undefined,
): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return Array.from(
      new Set(raw.filter((s): s is string => typeof s === 'string' && s.length > 0)),
    );
  }
  if (typeof raw === 'object') {
    const values = Object.values(raw).filter(
      (v): v is string => typeof v === 'string' && v.length > 0,
    );
    return Array.from(new Set(values));
  }
  return [];
}

function defToRow(def: AvatarDefinition): AvatarRow {
  // Pack the top-level tint + tints + published back into the geometry JSONB
  // on save. The in-memory def's geometry is kept clean by rowToDef, so we
  // don't have to worry about stale fields racing the new values.
  const hasTints = def.tints && Object.keys(def.tints).length > 0;
  const geometry: GlbGeometry & {
    tint?: string;
    tints?: Record<string, string>;
    published?: boolean;
  } = {
    ...def.geometry,
    ...(def.tint ? { tint: def.tint } : {}),
    ...(hasTints ? { tints: def.tints } : {}),
    ...(def.published ? { published: true } : {}),
  };
  return {
    id: def.id,
    name: def.name,
    description: def.description ?? null,
    tags: def.tags ?? null,
    geometry,
    animations: def.clips && def.clips.length > 0 ? def.clips : null,
    user_created: def.userCreated ?? true,
    uploaded_at: def.uploadedAt ?? Date.now(),
  };
}

class Registry {
  private defs = new Map<string, AvatarDefinition>();
  private listeners = new Set<RegistryListener>();
  /** Resolves once the first server hydrate completes (success or failure). */
  readonly ready: Promise<void>;

  constructor() {
    this.loadCache();
    this.ready = this.hydrateFromServer();

    // Cross-tab sync: when another tab (e.g. /admin) mutates the avatar
    // store, reload from cache + emit register/remove events so subscribers
    // in this tab (the room) react to deletions and new uploads. Server
    // changes from other browsers are picked up on next reload — add
    // Realtime later if same-session sync is needed.
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (e) => {
        if (e.key !== CACHE_KEY && e.key !== null) return;
        this.reconcileWith(this.readCache());
      });
    }
  }

  private readCache(): AvatarDefinition[] {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as Array<AvatarDefinition & {
        animations?: string[] | Record<string, string | undefined>;
      }>;
      // Cache entries written before the mood→clips rename carried an
      // `animations` object; flatten it back into a `clips` array so the
      // pre-hydrate window doesn't briefly drop the rotation pool.
      return parsed.map((d) => {
        if (d.clips && d.clips.length > 0) return d;
        const legacy = d.animations;
        if (!legacy) return d;
        return { ...d, clips: clipsFromStoredAnimations(legacy), animations: undefined };
      });
    } catch {
      return [];
    }
  }

  private writeCache(): void {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(this.list()));
    } catch {
      /* quota — ignore */
    }
  }

  private loadCache(): void {
    for (const d of this.readCache()) this.defs.set(d.id, d);
  }

  /** Pull authoritative state from the server, replace in-memory map, and
   *  emit register/remove for any diff vs. the cached state. Called once on
   *  construction and again whenever cross-tab cache changes. */
  private async hydrateFromServer(): Promise<void> {
    if (!supabase) return;
    const { data, error } = await supabase
      .from(AVATAR_TABLE)
      .select('*')
      .order('uploaded_at', { ascending: false });
    if (error) {
      console.warn('[avatar-registry] server hydrate failed; using local cache only.', error.message);
      return;
    }
    const fromServer = (data as AvatarRow[]).map(rowToDef);
    this.reconcileWith(fromServer);
    this.writeCache();
  }

  private reconcileWith(next: AvatarDefinition[]): void {
    const prev = new Map(this.defs);
    this.defs = new Map();
    for (const d of next) this.defs.set(d.id, d);
    for (const id of prev.keys()) {
      if (!this.defs.has(id)) this.emit({ type: 'remove', id });
    }
    for (const id of this.defs.keys()) {
      if (!prev.has(id)) this.emit({ type: 'register', id });
    }
  }

  /** Subscribe to register/remove events. Returns an unsubscribe fn.
   *  Consumers (room scene, join modal) use this to react when an
   *  avatar is deleted in admin — e.g. yank its 3D model out of the
   *  scene so the user doesn't keep seeing a "ghost" floor avatar. */
  subscribe(cb: RegistryListener): () => void {
    this.listeners.add(cb);
    return () => { this.listeners.delete(cb); };
  }

  private emit(event: { type: 'register' | 'remove'; id: string }): void {
    for (const cb of this.listeners) {
      try { cb(event); } catch (e) { console.warn('[avatar-registry] listener threw', e); }
    }
  }

  list(): AvatarDefinition[] {
    return Array.from(this.defs.values()).sort(
      (a, b) => (b.uploadedAt ?? 0) - (a.uploadedAt ?? 0),
    );
  }

  /** Same as list() but filtered to only published avatars. Use this for
   *  the room picker / NPC seeding so admin drafts don't leak to end
   *  users. Draft = published falsy (undefined or explicit false). */
  listPublished(): AvatarDefinition[] {
    return this.list().filter((d) => d.published === true);
  }

  get(id: string): AvatarDefinition | undefined {
    return this.defs.get(id);
  }

  /** Find the first avatar whose name or tags contain `query` (case
   *  insensitive). Lets the room look up "robot" without hardcoding an
   *  id that depends on which blob the user happened to upload. */
  findByName(query: string): AvatarDefinition | undefined {
    const q = query.toLowerCase();
    for (const d of this.defs.values()) {
      if (d.name.toLowerCase().includes(q)) return d;
      if (d.tags?.some((t) => t.toLowerCase().includes(q))) return d;
    }
    return undefined;
  }

  async register(def: AvatarDefinition): Promise<void> {
    if (supabase) {
      const { error } = await supabase
        .from(AVATAR_TABLE)
        .upsert(defToRow(def), { onConflict: 'id' });
      if (error) throw new Error(`Save failed: ${error.message}`);
    }
    this.defs.set(def.id, def);
    this.writeCache();
    this.emit({ type: 'register', id: def.id });
  }

  async remove(id: string): Promise<void> {
    if (!this.defs.has(id)) return;
    if (supabase) {
      const { error } = await supabase.from(AVATAR_TABLE).delete().eq('id', id);
      if (error) throw new Error(`Delete failed: ${error.message}`);
    }
    this.defs.delete(id);
    this.writeCache();
    this.emit({ type: 'remove', id });
  }
}

export const AvatarRegistry = new Registry();
